// ============================================
// Storage Service with Error Handling
// ============================================

import { STORAGE_CONFIG } from '@/constants';
import type {
  JLPTLevel,
  WordProgressMap,
  ErrorType,
  AppError
} from '@/types';

// ============================================
// Storage Error Handling
// ============================================

export class StorageError extends Error {
  type: ErrorType;
  recoverable: boolean;

  constructor(type: ErrorType, message: string, recoverable = true) {
    super(message);
    this.name = 'StorageError';
    this.type = type;
    this.recoverable = recoverable;
  }

  toAppError(): AppError {
    return {
      type: this.type,
      message: this.message,
      recoverable: this.recoverable
    };
  }
}

// ============================================
// Word Progress Write Queue (debounced)
// ============================================
//
// Per-answer hot path: instead of a synchronous JSON.stringify of the
// full ~600-entry progress map on every card answer, queue the (level,
// japanese) tuple and flush after a 400ms idle window. Callers must
// supply the full updated WordProgress at queue time via wordProgress
// signal; the flusher reads the latest signal value when it runs.
//
// Three guarantees the queue must keep:
//   1. Queue captures `level` at enqueue time — survives level switches
//      mid-debounce. Earlier design with a stateful currentLevel field
//      caused Finding H.
//   2. Flush writes the WHOLE map for each dirty (level), not per-key —
//      localStorage.setItem is one syscall regardless of map size and
//      per-key writes would race their own read-modify-write.
//   3. Synchronous flush on `pagehide` — no rIC/setTimeout wrapper, the
//      tab closes before async callbacks fire.
//
// requestIdleCallback is preferred when available; on Safari (no RIC)
// we drop the idle wrapper entirely. setTimeout(0) is NOT a useful
// fallback — 4ms clamp + same task queue defeats the debounce.
const FLUSH_DEBOUNCE_MS = 400;
const QUEUE_SIZE_GUARD = 10;

type ProgressMapReader = (level: JLPTLevel) => WordProgressMap;
type FlushErrorHandler = (err: unknown) => void;
type FlushSuccessHandler = (level: JLPTLevel, map: WordProgressMap) => void;

interface QueueState {
  // Set of dirty keys per level. Values are japanese strings; we don't
  // store the WordProgress objects because the source of truth is the
  // wordProgress signal (read at flush time via `readMap`).
  dirty: Map<JLPTLevel, Set<string>>;
  timer: ReturnType<typeof setTimeout> | null;
  idleHandle: number | null;
  pagehideHandler: (() => void) | null;
}

// ============================================
// Storage Service
// ============================================
//
// API note: every word-progress operation takes `level` explicitly.
// There is no stateful current-level field — earlier versions had one,
// and a missed update path silently leaked N5 progress into N4 storage
// keys (see plan Finding H). Per-call level eliminates that bug class.

export class StorageService {
  private queue: QueueState = {
    dirty: new Map(),
    timer: null,
    idleHandle: null,
    pagehideHandler: null
  };

  // Source-of-truth reader. Defaults to reading from this service's own
  // localStorage view; in tests / from signals.ts we override to read
  // from the wordProgress signal so the queue picks up the latest values.
  private readMap: ProgressMapReader = level => this.getWordProgress(level);

  // Optional handler for storage errors during a flush — wired by
  // signals.ts to surface a one-time toast when localStorage is full
  // or disabled. Without this, pagehide/timer-triggered flushes throw
  // into the void (no event handler catches them).
  private onFlushError: FlushErrorHandler | null = null;

  // Optional post-flush success hook — wired by SyncService (W3) to push
  // the just-saved level map to the cloud. Invoked AFTER a successful
  // saveWordProgress for each dirty level, never when the save threw.
  // The hook is fire-and-forget from the flush's perspective: any error
  // it throws is routed to onFlushError and never breaks or blocks the
  // synchronous flush.
  private onFlushSuccess: FlushSuccessHandler | null = null;

  /**
   * Provide a custom reader for the source-of-truth progress map. Wired
   * by signals.ts so the flusher reads the wordProgress signal value.
   */
  setProgressReader(reader: ProgressMapReader): void {
    this.readMap = reader;
  }

  /**
   * Register a handler called when a queued flush throws (typically
   * QuotaExceededError from localStorage.setItem). Use this to surface
   * a user-visible notification, since pagehide/timer flushes have no
   * caller to catch the error.
   */
  setFlushErrorHandler(handler: FlushErrorHandler | null): void {
    this.onFlushError = handler;
  }

  /**
   * Register a handler called after a queued flush SUCCESSFULLY saves a
   * level's map to localStorage. Wired by SyncService (W3) to piggyback a
   * cloud upsert onto the same flush — no `await supabase` on the answer
   * hot path. The hook must not break or block the synchronous flush:
   * its errors are routed to onFlushError, and it never runs for a level
   * whose save threw.
   */
  setFlushSuccessHandler(handler: FlushSuccessHandler | null): void {
    this.onFlushSuccess = handler;
  }
  // ==========================================
  // Quota Check
  // ==========================================

  async checkStorageQuota(): Promise<{ used: number; quota: number; percentUsed: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        const used = estimate.usage || 0;
        const quota = estimate.quota || 0;
        const percentUsed = quota > 0 ? used / quota : 0;
        return { used, quota, percentUsed };
      } catch {
        return { used: 0, quota: 0, percentUsed: 0 };
      }
    }
    return { used: 0, quota: 0, percentUsed: 0 };
  }

  isQuotaNearFull(): Promise<boolean> {
    return this.checkStorageQuota().then(
      ({ percentUsed }) => percentUsed >= STORAGE_CONFIG.quotaWarningThreshold
    );
  }

  // ==========================================
  // Safe Storage Operations
  // ==========================================

  private getItem<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return defaultValue;
      return JSON.parse(item) as T;
    } catch (error) {
      console.warn(`Failed to parse localStorage item: ${key}`, error);
      return defaultValue;
    }
  }

  private setItem<T>(key: string, value: T): boolean {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        throw new StorageError(
          'QUOTA_EXCEEDED' as ErrorType,
          'Storage quota exceeded. Please clear some data.',
          true
        );
      }
      throw new StorageError(
        'STORAGE_UNAVAILABLE' as ErrorType,
        'Failed to save data to storage.',
        false
      );
    }
  }

  private removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove localStorage item: ${key}`, error);
    }
  }

  // ==========================================
  // Word Progress Operations
  // ==========================================

  private getProgressKey(level: JLPTLevel): string {
    return STORAGE_CONFIG.keys.wordProgress(level);
  }

  getWordProgress(level: JLPTLevel): WordProgressMap {
    return this.getItem<WordProgressMap>(this.getProgressKey(level), {});
  }

  saveWordProgress(level: JLPTLevel, progress: WordProgressMap): boolean {
    return this.setItem(this.getProgressKey(level), progress);
  }

  // ==========================================
  // Preferences
  // ==========================================

  getPreferences<T extends Record<string, unknown>>(defaults: T): T {
    return this.getItem(STORAGE_CONFIG.keys.preferences, defaults);
  }

  savePreferences(prefs: Record<string, unknown>): boolean {
    const existing = this.getPreferences({});
    return this.setItem(STORAGE_CONFIG.keys.preferences, { ...existing, ...prefs });
  }

  // ==========================================
  // Queued write API
  // ==========================================

  /**
   * Enqueue a word-progress write for `level`. The queue debounces
   * writes by 400ms; ≥ 10 dirty entries forces an immediate flush.
   * The actual write is read-the-current-map-at-flush-time, so callers
   * must update wordProgress signal BEFORE calling this.
   *
   * Throws StorageError synchronously if quota is exceeded on the
   * forced/sized flush path — caller (signals.ts) catches and shows
   * the storage-degraded toast.
   */
  queueWordProgressWrite(level: JLPTLevel, japanese: string): void {
    let levelSet = this.queue.dirty.get(level);
    if (!levelSet) {
      levelSet = new Set();
      this.queue.dirty.set(level, levelSet);
    }
    levelSet.add(japanese);

    // Install pagehide handler exactly once.
    if (!this.queue.pagehideHandler && typeof window !== 'undefined') {
      this.queue.pagehideHandler = () => this.flushPending();
      window.addEventListener('pagehide', this.queue.pagehideHandler);
    }

    // Queue-size guard: too many dirty entries → flush now (defends
    // against rapid-answer-then-close where iOS Safari may kill the
    // tab mid-stringify).
    const totalDirty = this.dirtyCount();
    if (totalDirty >= QUEUE_SIZE_GUARD) {
      this.flushPending();
      return;
    }

    this.scheduleFlush();
  }

  /**
   * Synchronously flush all queued writes. Called by pagehide handler,
   * by the queue-size guard, and by callers that need a write barrier
   * (e.g., level-switch handler before re-hydrating the new level).
   */
  flushPending(): void {
    this.cancelScheduledFlush();
    if (this.queue.dirty.size === 0) return;

    // Snapshot + clear the dirty map first so that any error mid-flush
    // doesn't leave entries marked dirty for a level we already wrote.
    const dirty = this.queue.dirty;
    this.queue.dirty = new Map();

    for (const level of dirty.keys()) {
      let map: WordProgressMap;
      try {
        map = this.readMap(level);
        this.saveWordProgress(level, map);
      } catch (err) {
        // pagehide/timer-triggered flushes have no caller to catch
        // QuotaExceededError; route to the registered handler. Skip the
        // success hook for this level — the save did not happen.
        if (this.onFlushError) this.onFlushError(err);
        else console.warn('Storage flush failed', err);
        continue;
      }

      // Save succeeded — fire the post-flush success hook (cloud push).
      // Guard it in try/catch so a hook error can never break or block
      // the flush; route hook errors to onFlushError.
      if (this.onFlushSuccess) {
        try {
          this.onFlushSuccess(level, map);
        } catch (err) {
          if (this.onFlushError) this.onFlushError(err);
          else console.warn('Flush success hook failed', err);
        }
      }
    }
  }

  private dirtyCount(): number {
    let n = 0;
    for (const set of this.queue.dirty.values()) n += set.size;
    return n;
  }

  private scheduleFlush(): void {
    this.cancelScheduledFlush();
    this.queue.timer = setTimeout(() => {
      this.queue.timer = null;
      this.runIdleFlush();
    }, FLUSH_DEBOUNCE_MS);
  }

  private cancelScheduledFlush(): void {
    if (this.queue.timer != null) {
      clearTimeout(this.queue.timer);
      this.queue.timer = null;
    }
    if (this.queue.idleHandle != null && typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(this.queue.idleHandle);
      this.queue.idleHandle = null;
    }
  }

  private runIdleFlush(): void {
    // requestIdleCallback when available; on Safari (no RIC) we don't
    // wrap — setTimeout(0) is the wrong fallback (4ms clamp + same task
    // queue defeats the debounce intent).
    if (typeof requestIdleCallback === 'function') {
      this.queue.idleHandle = requestIdleCallback(() => {
        this.queue.idleHandle = null;
        this.flushPending();
      });
    } else {
      this.flushPending();
    }
  }

  // ==========================================
  // Cleanup
  // ==========================================

  clearAllData(): void {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_CONFIG.prefix));
    keys.forEach(key => this.removeItem(key));
  }
}

// ============================================
// Singleton Export
// ============================================

export const storageService = new StorageService();
